import max from 'ramda/src/max.js'
import compose from 'ramda/src/compose.js'
import replace from 'ramda/src/replace.js'
import numberConverter from 'number-to-words';
import {FULLNAME_PATTERN} from './Constants.js';

const check = function ( str ) {
    if ( typeof str !== 'string' ) {
        try {
            str = String( str );
        }
        catch ( err ) {
            console.error( `Invalid argument: StringUtils requires a string argument but received ${str} instead` );
            str = '';
        }
    }

    return str;
};

// Remove trailing period - some OSs will add a period at end of string on double press of [SPACE] key
export const sanitizeTagString = ( value ) => value.trim().replace( /(.*)\.$/, '$1' );

export const validatePostalCode = function ( postalCode ) {
    const regex = /^\d{5}(-\d{4})?$/;
    return regex.test( postalCode );
};

export const validateEmail = function ( email ) {
    const str = check( email );
    const regex = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)+$/;
    return regex.test( str );
};

export const validateFullName = function ( str ) {
    return FULLNAME_PATTERN.test( str );
}

export function formatPhoneUS ( phoneNumber ) {
    const match = /(\d{3})(\d{3})(\d{4})/.exec( phoneNumber );
    if ( !match || match.length !== 4 ) {
        return phoneNumber;
    }

    return `(${match[1]}) ${match[2]}-${match[3]}`;
}

export function trimFormData ( obj = {} ) {
    const newObj = {};

    for ( const key in obj ) {
        if ( typeof obj[key] !== 'string' ) {
            newObj[key] = obj[key];
            continue;
        }
        newObj[key] = obj[key]
            // remove auto added periods and white space
            .replace( /\.\s+$/, '' )
            // replace meta whitespace characters with normal whitespaces
            .replace( /\s+/g, ' ' )
            .trim();
    }

    return newObj;
}

export function reverseString ( s ) {
    const str = check( s );
    return str.split( '' ).reverse().join( '' );
}

export function truncateStringByChar ( s, maxLen = 100 ) {
    const str = check( s );

    const len = max( 0, maxLen );
    if ( str.length > len ) {
        // find last word boundary starting at specified length and trim
        return str.substr( 0, len ).replace( /\s+\w+$/, '' );
    }

    return str;
}

export function truncateStringByLine ( s, maxLen = 3 ) {
    const str = check( s );

    // match lines with at least 1 carriage return
    const matchedLines = str.match( /.*[\r\n]{1,2}/gm );
    const len = max( 0, maxLen );
    if ( matchedLines && matchedLines.length >= len ) {
        return matchedLines
            .slice( 0, len )
            .reduce( ( acc, next ) => acc + next, '' )
            .trim();
    }

    return str;
}

export function truncateString ( s, maxCharLen, maxLineLen ) {
    const str = check( s );

    return truncateStringByChar(
        truncateStringByLine( str, maxLineLen ),
        maxCharLen
    );
}

export const toTitleCase = ( s ) => {
    const str = check( s );

    return str.replace( /\w\S*/g, ( txt ) => {
        return txt.charAt( 0 ).toUpperCase() + txt.substr( 1 ).toLowerCase();
    } );
};

export const toCamelCase = ( str ) => {
    return str.replace( /(?:^\w|[A-Z]|\b\w|\s+)/g, function ( match, index ) {
        if ( +match === 0 ) {
            return '';
        } // or if (/\s+/.test(match)) for white spaces
        return index === 0 ? match.toLowerCase() : match.toUpperCase();
    } );
}

export const toInitials = ( str ) => {
    if ( !str ) {
        return null;
    }
    const parts = str.split( ' ' );
    let alias = parts[0].charAt( 0 );
    if ( parts[1] ) {
        alias += parts[1].charAt( 0 );
    }
    return alias.toUpperCase();
}

/**
 * Format bytes as human-readable text.
 *
 * @param bytes Number of bytes.
 * @param si True to use metric (SI) units, aka powers of 1000. False to use
 *           binary (IEC), aka powers of 1024.
 * @param dp Number of decimal places to display.
 *
 * @return Formatted string.
 */
export const toFileSize = ( bytes, si = true, dp = 1 ) => {
    const thresh = si ? 1000 : 1024;

    if ( Math.abs( bytes ) < thresh ) {
        return bytes + ' B';
    }

    const units = si
        ? ['kB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB']
        : ['KiB', 'MiB', 'GiB', 'TiB', 'PiB', 'EiB', 'ZiB', 'YiB'];
    let u = -1;
    const r = 10 ** dp;

    do {
        bytes /= thresh;
        ++u;
    }
    while ( Math.round( Math.abs( bytes ) * r ) / r >= thresh && u < units.length - 1 );

    return `${bytes.toFixed( dp )} ${units[u]}`;
}

export const toLegibleStringList = list => {
    const delimiter = list.length > 2 ? ', ' : ' ';
    const formattedList = list.map( ( item, i ) => {
        if ( i === 0 ) {
            return item.substr( 0, 1 ).toUpperCase() + item.substr( 1, item.length ).toLowerCase();
        }
        if ( i === list.length - 1 ) {
            return `and ${item.toLowerCase()}`;
        }

        return item.toLowerCase();
    } );

    return formattedList.join( delimiter );
};

export const toSlug = ( str ) => {
    // extract numbers from beginning of string
    const numPattern = /(^[0-9]+\.+?)([^0-9].*)/;

    // if slug begins with number we need to convert it to a word to become a valid HTMLElement id
    const matches = str.match( numPattern );
    if ( matches && !isNaN( matches[1] ) ) {
        const numWord = numberConverter.toWords( str.replace( numPattern, '$1' ) );
        str = str.replace( numPattern, `${numWord} $2` );
    }

    return str.toLowerCase()
        .replace( /[^\w ]+/g, '' )
        .replace( / +/g, '-' );
}

export const isValidSelector = (query) => {
    try {
        document.createDocumentFragment().querySelector(query);
    }
    catch(err) {
        return false;
    }

    return true;
}

// only replace "-" or "/" that appear between words (word chars) leaving other values
// such as SKU or Serial Numbers unchanged that may have a combination of numbers and word chars
export const wordsFromSlug = compose( replace( /(?!=[a-z][^0-9]+)[-/](?=[a-z][^0-9]+)/gi, ' ' ), decodeURIComponent );

export const toTitleFromSlug = compose( toTitleCase, wordsFromSlug );
